import React, { useState } from 'react';
import { Div, DivProps, SupportedColor } from '@gaiads/telia-react-component-library';

import { EditableItemInputProps } from './EditableItemInput/EditableItemInput';
import EditableItemInput from './EditableItemInput/EditableItemInput';
import EditableItemDropdown, { EditableItemDropdownProps } from './EditableItemDropdown/EditableItemDropdown';
import { EditableItemTextAreaProps } from './EditableItemTextArea/EditableItemTextArea';
import EditableItemTextArea from './EditableItemTextArea/EditableItemTextArea';
import EditableItemIcons from './EditableItemIcons/EditableItemIcons';
import { HTMLElementProps } from '../../utils/types';

import './EditableItem.scss';

export interface BaseEditableItemFieldProps {
  error?: string;
  isEditingItem?: boolean;
  setIsEditingItem?: (isEditing: boolean) => void;
  isReference?: boolean;
  showSave?: boolean;
}

interface EditableItemProps {
  children?: JSX.Element | (JSX.Element | undefined)[];
  padding?: DivProps['padding'];
  backgroundColor?: SupportedColor;
  backgroundHoverColor?: SupportedColor;
  divProps?: Partial<DivProps & HTMLElementProps>;
}

const EditableItemWithoutSubcomponents = (props: EditableItemProps) => {
  const { divProps, backgroundColor, backgroundHoverColor, children } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);

  const handleMouseOver = () => {
    setIsHovered(true);
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
  };
  const handleItemEdit = (isEditing: boolean) => {
    setIsHovered(false);
    setIsEditingItem(isEditing);
  };

  const getContent = () =>
    React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        switch (child.type) {
          case EditableItemInput:
            return <EditableItemInput setIsEditingItem={handleItemEdit} {...(child.props as EditableItemInputProps)} />;
          case EditableItemDropdown:
            return (
              <EditableItemDropdown isEditingItem={isEditingItem} setIsEditingItem={handleItemEdit} {...(child.props as EditableItemDropdownProps)} />
            );
          case EditableItemTextArea:
            return <EditableItemTextArea setIsEditingItem={handleItemEdit} {...(child.props as EditableItemTextAreaProps)} />;
          default:
            return child;
        }
      } else return child;
    });

  return (
    <Div
      backgroundColor={!isEditingItem && isHovered ? backgroundHoverColor : backgroundColor}
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      {...divProps}
      className={['editable-field__container', !isEditingItem && isHovered && 'editable-field__container--hover', divProps?.className]}
    >
      {getContent()}
    </Div>
  );
};

const EditableItem: typeof EditableItemWithoutSubcomponents & {
  Input: typeof EditableItemInput;
  Dropdown: typeof EditableItemDropdown;
  TextArea: typeof EditableItemTextArea;
  Icons: typeof EditableItemIcons;
} = ({ ...props }) => <EditableItemWithoutSubcomponents {...props} />;

EditableItem.Input = EditableItemInput;
EditableItem.Dropdown = EditableItemDropdown;
EditableItem.TextArea = EditableItemTextArea;
EditableItem.Icons = EditableItemIcons;

export default EditableItem;
